import Rellax from 'rellax';
import imageValues from '../../lib/utils/imageValues';
import renderImageHTML from '../../lib/utils/renderImageHTML';
import { truncateWords } from '../../lib/utils/truncateWords';
import { getCurrentBreakpoint as getViewportBreakpoint, isMobileDevice } from '../../lib/utils/viewport';
import { debounce } from '../../lib/utils/debounce';
import { ready, onceInitialized } from '../../lib/lifecycle/ready';

// Mobile detection (borrowed from parallax-container pattern)
window.mobileCheck = function() {
  let check = false;
  (function(a) { 
    if (/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; 
  })(navigator.userAgent || navigator.vendor || window.opera);
  return check;
};

window.isMobile = isMobileDevice();

// Get current breakpoint based on window width
function getCurrentBreakpoint() {
  return getViewportBreakpoint(window.innerWidth);
}

// Check if fixed background is enabled for current breakpoint
function isEnabledForBreakpoint(container, breakpoint) {
  const breakpoints = container.dataset.fixedBgBreakpoints;
  if (!breakpoints) return false;
  
  try {
    const breakpointData = JSON.parse(breakpoints);
    return breakpointData[breakpoint] === true;
  } catch (e) {
    return false;
  }
}

// Initialize fixed backgrounds for query loop blocks
function initFixedBackgrounds() {
  if (window.isMobile) {
    // Check if any container has mobile enabled
    const containers = document.querySelectorAll('[data-has-fixed-bg="true"]');
    let hasMobileEnabled = false;
    containers.forEach(container => {
      const breakpoints = container.dataset.fixedBgBreakpoints;
      if (breakpoints) {
        try {
          const breakpointData = JSON.parse(breakpoints);
          if (breakpointData['576'] === true) {
            hasMobileEnabled = true;
          }
        } catch (e) {}
      }
    });
    if (!hasMobileEnabled) return;
  }

  const containers = document.querySelectorAll('[data-has-fixed-bg="true"]');
  const currentBreakpoint = getCurrentBreakpoint();

  containers.forEach(container => {
    // Get all background elements (image, video, and gradient)
    const bgElements = [
      container.querySelector('modulr-bg'),
      container.querySelector('video-bg'),
      container.querySelector('.modulr-background-gradient')
    ].filter(el => el !== null);

    if (bgElements.length === 0) {
      return;
    }

    // Check if enabled for current breakpoint
    if (!isEnabledForBreakpoint(container, currentBreakpoint)) {
      // Destroy existing Rellax instances if any
      bgElements.forEach(bgElement => {
        if (bgElement.rellax) {
          bgElement.rellax.destroy();
          bgElement.rellax = null;
        }
      });
      return;
    }

    // Skip on mobile if not enabled for mobile breakpoint
    if (window.isMobile && currentBreakpoint === '576' && !isEnabledForBreakpoint(container, '576')) {
      bgElements.forEach(bgElement => {
        if (bgElement.rellax) {
          bgElement.rellax.destroy();
          bgElement.rellax = null;
        }
      });
      return;
    }

    const mode = container.dataset.fixedBgMode || 'fixed';
    const speed = parseFloat(container.dataset.fixedBgSpeed || -1);
    
    // For fixed mode, we need the background to counteract scroll exactly
    // Rellax with center:false and a negative speed creates the fixed effect
    const rellaxConfig = {
      center: false,
      wrapper: null,
      relativeToWrapper: false,
      round: true,
      vertical: true,
      horizontal: false
    };

    // Initialize Rellax for each background element
    bgElements.forEach(bgElement => {
      // Don't reinitialize if already initialized
      if (bgElement.rellax) return;

      // Add rellax class and speed attribute
      bgElement.classList.add('rellax');
      bgElement.setAttribute('data-rellax-speed', speed);

      // Initialize Rellax
      try {
        bgElement.rellax = new Rellax(bgElement, rellaxConfig);
      } catch (error) {
        console.error('Fixed background: Error initializing Rellax', error, bgElement);
      }
    });
  });
}

// Destroy all Rellax instances
function destroyFixedBackgrounds() {
  const containers = document.querySelectorAll('[data-has-fixed-bg="true"]');
  containers.forEach(container => {
    const bgElements = [
      container.querySelector('modulr-bg'),
      container.querySelector('video-bg'),
      container.querySelector('.modulr-background-gradient')
    ].filter(el => el !== null);

    bgElements.forEach(bgElement => {
      if (bgElement.rellax) {
        bgElement.rellax.destroy();
        bgElement.rellax = null;
        bgElement.classList.remove('rellax');
        bgElement.removeAttribute('data-rellax-speed');
      }
    });
  });
}

// Initialize query loop blocks
function initQueryLoops(scope) {
  const root = scope || document;
  const queryLoopElements = root.querySelectorAll('[data-query-params]');

  queryLoopElements.forEach((element) => {
    if (!onceInitialized(element, 'query-loop')) return;
    const queryParams = JSON.parse(element.dataset.queryParams || '{}');
    const paginationType = element.dataset.paginationType || 'none';
    const excerptWordLimit = element.dataset.excerptWordLimit ? parseInt(element.dataset.excerptWordLimit) : null;
    
    // Detect if this query loop is inside a carousel block
    const isCarouselChild = element.closest('.wp-block-modulr-carousel') !== null;
    
    const postsContainer = element.querySelector('.query-loop-posts');
    const template = element.querySelector('.query-loop-template');
    const loadingIndicator = element.querySelector('.query-loop-loading');
    
    if (!postsContainer || !template) return;
    
    let currentPage = 1;
    let selectedCategories = []; // Store category slugs for API
    const showCategoryFilter = element.dataset.showCategoryFilter === 'true';
    const categoryFilterContainer = element.querySelector('.query-loop-category-filter');

    // Helper function to decode HTML entities
    const decodeHtmlEntities = (text) => {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    };

    // Initialize category filter if enabled
    if (showCategoryFilter && categoryFilterContainer) {
      // Fetch categories from WordPress REST API
      fetch('/wp-json/wp/v2/categories?per_page=100&orderby=name&order=asc')
        .then(response => response.json())
        .then(categories => {
          if (categories && categories.length > 0) {
            // Intro paragraph with large font size (categories heading)
            const introParagraph = document.createElement('p');
            introParagraph.className = 'has-large-font-size';
            introParagraph.textContent = 'Categories';

            // Create checkbox container
            const checkboxContainer = document.createElement('div');
            checkboxContainer.className = 'category-filter-checkboxes';

            // Create "Clear All" button (appended after checkboxes)
            const clearButton = document.createElement('button');
            clearButton.className = 'category-filter-clear';
            clearButton.textContent = 'Clear All';
            clearButton.type = 'button';
            clearButton.style.cssText = 'padding: 0.5em 1em; cursor: pointer;';

            // Render checkboxes for each category
            categories.forEach(category => {
              const label = document.createElement('label');
              label.className = 'category-filter-checkbox';
              label.style.cssText = 'cursor: pointer;';
              
              const checkbox = document.createElement('input');
              checkbox.type = 'checkbox';
              checkbox.value = category.slug; // Use slug for value
              checkbox.dataset.categoryId = category.id; // Store ID for reference
              checkbox.className = 'category-checkbox';
              
              const span = document.createElement('span');
              // Decode HTML entities in category name
              span.textContent = decodeHtmlEntities(category.name);
              span.style.cssText = 'margin-left: 0.5em;';
              
              label.appendChild(checkbox);
              label.appendChild(span);
              
              // Add change event handler
              checkbox.addEventListener('change', () => {
                if (checkbox.checked) {
                  if (!selectedCategories.includes(category.slug)) {
                    selectedCategories.push(category.slug);
                  }
                } else {
                  selectedCategories = selectedCategories.filter(slug => slug !== category.slug);
                }
                
                // Reset to page 1 when filter changes
                currentPage = 1;
                
                // Rerun query with updated categories
                fetchPosts(1);
              });
              
              checkboxContainer.appendChild(label);
            });
            
            // Add clear button functionality
            clearButton.addEventListener('click', () => {
              selectedCategories = [];
              checkboxContainer.querySelectorAll('.category-checkbox').forEach(cb => {
                cb.checked = false;
              });
              currentPage = 1;
              fetchPosts(1);
            });
            
            categoryFilterContainer.appendChild(introParagraph);
            categoryFilterContainer.appendChild(checkboxContainer);
            categoryFilterContainer.appendChild(clearButton);
          }
        })
        .catch(error => {
          console.error('Error fetching categories:', error);
        });
    }

    const fetchPosts = (page = 1) => {
      if (loadingIndicator) {
        loadingIndicator.style.display = 'block';
      }
      
      const params = new URLSearchParams({
        post_type: queryParams.postType || 'post',
        posts_per_page: queryParams.postsPerPage || 10,
        order: queryParams.order || 'DESC',
        orderby: queryParams.orderby || 'date',
        page: page,
      });

      // Use selected categories from filter if available, otherwise use queryParams.categories
      const categoriesToUse = showCategoryFilter && selectedCategories.length > 0 
        ? selectedCategories 
        : (queryParams.categories || []);
      
      if (categoriesToUse.length > 0) {
        params.append('category', categoriesToUse.join(','));
      }

      if (queryParams.tags?.length) {
        params.append('tag', queryParams.tags.join(','));
      }

      if (queryParams.author) {
        params.append('author', queryParams.author);
      }

      if (queryParams.offset) {
        params.append('offset', queryParams.offset);
      }

      if (excerptWordLimit) {
        params.append('excerpt_word_limit', excerptWordLimit);
      }

      const standardUrl = `/wp-json/modulr/v1/posts?${params.toString()}`;
      const urlS = new URLSearchParams(window.location.search).get('s');
      const semanticMode = element.dataset.useSemanticSearch || 'auto';
      const semanticAvailable =
        typeof window.wpSemanticSearch !== 'undefined' &&
        window.wpSemanticSearch.enabled &&
        window.wpSemanticSearch.restUrl &&
        window.wpSemanticSearch.nonce;

      let wantsSemantic =
        semanticAvailable &&
        urlS &&
        urlS.trim() !== '' &&
        semanticMode !== 'never' &&
        (semanticMode === 'auto' || semanticMode === 'always');

      if (semanticMode === 'always' && !semanticAvailable && typeof console !== 'undefined' && console.warn) {
        console.warn(
          '[modulr/query-loop] useSemanticSearch is "always" but WP Semantic Search is not available; using standard REST.'
        );
      }

      const loadJson = (response) => {
        if (wantsSemantic && response.status === 503) {
          wantsSemantic = false;
          return fetch(standardUrl).then((r) => r.json());
        }
        return response.json();
      };

      const runRequest = () => {
        if (!wantsSemantic) {
          return fetch(standardUrl).then(loadJson);
        }
        const semanticParams = new URLSearchParams({
          s: urlS.trim(),
          page: String(page),
          per_page: String(queryParams.postsPerPage || 10),
        });
        if (queryParams.postType) {
          semanticParams.append('post_types', queryParams.postType);
        }
        if (excerptWordLimit) {
          semanticParams.append('excerpt_word_limit', String(excerptWordLimit));
        }
        const semanticUrl = `${window.wpSemanticSearch.restUrl}/search?${semanticParams.toString()}`;
        return fetch(semanticUrl, {
          credentials: 'same-origin',
          headers: {
            'X-WP-Nonce': window.wpSemanticSearch.nonce,
          },
        }).then(loadJson);
      };

      runRequest()
        .then(data => {
          // Clear container: always for numbered pagination, only page 1 for load-more
          if (paginationType === 'numbered' || page === 1) {
            postsContainer.innerHTML = '';
          }
          
          if (data.posts && data.posts.length > 0) {
            data.posts.forEach(post => {
              renderPost(post);
            });
          } else if (page === 1) {
            // Show no results message on first page
            const noResultsMessage = element.dataset.noResultsMessage || 'No posts found.';
            postsContainer.innerHTML = `<div class="query-loop-no-results">${noResultsMessage}</div>`;
          }
          
          if (loadingIndicator) {
            loadingIndicator.style.display = 'none';
          }
          
          // Initialize carousel if this query loop is inside a carousel
          if (isCarouselChild && data.posts && data.posts.length > 0 && page === 1) {
            // Add carousel classes and attributes to posts container
            // Only add 'init' class if carousel hasn't been initialized yet
            if (!postsContainer.classList.contains('f-carousel')) {
              const parentFCarousel = postsContainer.closest('.f-carousel');

              if (parentFCarousel) {
                const dataAttrs = [
                  'transition', 'duration', 'arrow-icon-url', 'autoplay',
                  'slides-per-page', 'slide-width', 'slide-gap', 'center',
                  'fill', 'infinite', 'show-progress', 'show-dots', 'dots-padding-bottom'
                ];
                dataAttrs.forEach(attr => {
                  const value = parentFCarousel.getAttribute(`data-${attr}`);
                  if (value !== null) postsContainer.setAttribute(`data-${attr}`, value);
                });
                parentFCarousel.classList.remove('init');
              }

              postsContainer.classList.add('f-carousel', 'init');

              if (typeof modulrCarouselInit === 'function') {
                setTimeout(() => modulrCarouselInit(), 100);
              }
            }
          }
          
          // Skip pagination when inside carousel
          if (!isCarouselChild && paginationType !== 'none') {
            updatePagination(data.pagination);
          }
        })
        .catch(error => {
          console.error('Error fetching posts:', error);
          if (loadingIndicator) {
            loadingIndicator.style.display = 'none';
          }
        });
    };

    const renderPost = (post) => {
      const templateContent = template.querySelector('div');
      if (!templateContent) return;
      
      const postElement = templateContent.cloneNode(true);
      
      // Populate featured image
      const featuredImageBlock = postElement.querySelector('.wp-block-modulr-post-featured-image, .wp-modulr-block-post-featured-image, .post-featured-image, [data-post-field="featured-image"]');
      if (featuredImageBlock && post.featured_image) {
        // Ensure the block has the correct class name
        featuredImageBlock.classList.add('wp-block-modulr-post-featured-image');
        
        const attrs = JSON.parse(featuredImageBlock.dataset.attributes || '{}');
        const isLink = featuredImageBlock.dataset.isLink === 'true';
        
        const imageHTML = buildModulrImage(post.featured_image, attrs);
        featuredImageBlock.innerHTML = isLink ? 
          `<a href="${post.permalink}">${imageHTML}</a>` : 
          imageHTML;
      }
      
      // Populate title
      const titleBlock = postElement.querySelector('[data-post-field="title"]');
      if (titleBlock) {
        const isLink = titleBlock.dataset.isLink === 'true';
        const headingLevel = titleBlock.dataset.headingLevel || '2';
        const headingTag = `h${headingLevel}`;
        const headingElement = titleBlock.querySelector(headingTag);
        if (headingElement) {
          const titleContent = isLink ? 
            `<a href="${post.permalink}">${post.title}</a>` : 
            post.title;
          headingElement.innerHTML = titleContent;
        }
      }
      
      // Populate excerpt (decode all HTML entities, apply word limit if set)
      const excerptBlock = postElement.querySelector('[data-post-field="excerpt"]');
      if (excerptBlock) {
        const pElement = excerptBlock.querySelector('p');
        if (pElement) {
          let text = decodeHtmlEntities(post.excerpt || '');
          if (excerptWordLimit && excerptWordLimit > 0) {
            text = truncateWords(text, excerptWordLimit);
          }
          pElement.textContent = text;
        }
      }
      
      // Populate date
      const dateBlock = postElement.querySelector('[data-post-field="date"]');
      if (dateBlock) {
        const timeElement = dateBlock.querySelector('time');
        if (timeElement) {
          const dateFormat = dateBlock.dataset.dateFormat || 'F j, Y';
          const showTime = dateBlock.dataset.showTime === 'true';
          const isLink = dateBlock.dataset.isLink === 'true';
          
          // Format date - for now use the date from API, could format client-side if needed
          const dateText = post.date || '';
          timeElement.setAttribute('datetime', post.date_raw || '');
          timeElement.textContent = dateText;
          
          if (isLink && post.permalink) {
            const link = document.createElement('a');
            link.href = post.permalink;
            link.textContent = dateText;
            timeElement.innerHTML = '';
            timeElement.appendChild(link);
          }
        }
      }
      
      // Populate meta
      const metaBlock = postElement.querySelector('[data-post-field="meta"]');
      if (metaBlock) {
        const metaType = metaBlock.dataset.metaType;
        const separator = metaBlock.dataset.separator || ', ';
        const metaDiv = metaBlock.querySelector('div');
        
        if (metaDiv) {
          if (metaType === 'categories' && post.categories) {
            const categoriesHTML = post.categories.map(cat => {
              const link = cat.link || '#';
              const name = cat.name || cat;
              return `<a href="${link}">${name}</a>`;
            }).join(separator);
            metaDiv.innerHTML = categoriesHTML;
          } else if (metaType === 'tags' && post.tags) {
            const tagsHTML = post.tags.map(tag => {
              const link = tag.link || '#';
              const name = tag.name || tag;
              return `<a href="${link}">${name}</a>`;
            }).join(separator);
            metaDiv.innerHTML = tagsHTML;
          } else if (metaType === 'author' && post.author) {
            metaDiv.textContent = post.author;
          }
        }
      }

      const pdfBadgeEl = postElement.querySelector('[data-post-field="pdf-badge"]');
      if (pdfBadgeEl) {
        if (post.is_pdf) {
          pdfBadgeEl.style.display = '';
          pdfBadgeEl.textContent = pdfBadgeEl.dataset.label || 'PDF';
        } else {
          pdfBadgeEl.style.display = 'none';
        }
      }

      if (post.is_pdf && window.wpSemanticSearch && window.wpSemanticSearch.pdfLinkNewTab) {
        const pdfLinks = postElement.querySelectorAll('a[href]');
        pdfLinks.forEach((a) => {
          a.setAttribute('target', '_blank');
          a.setAttribute('rel', 'noopener');
        });
      }
      
      // If in carousel mode, wrap in carousel-slide div
      if (isCarouselChild) {
        const slideWrapper = document.createElement('div');
        slideWrapper.className = 'f-carousel__slide';
        slideWrapper.appendChild(postElement);
        postsContainer.appendChild(slideWrapper);
      } else {
        postsContainer.appendChild(postElement);
      }
    };

    const buildModulrImage = (imageData, attrs) => {
      if (!imageData || !imageData.url) return '';
      
      // Prepare attributes for imageValues calculation
      // Include image dimensions from API if available
      const imageAttrs = {
        desktopRatio: attrs.desktopRatio || 75,
        tabletRatio: attrs.tabletRatio,
        mobileRatio: attrs.mobileRatio,
        imageMaxWidth: attrs.imageMaxWidth || 1920,
        imageFocalPoint: attrs.imageFocalPoint || { x: 0.5, y: 0.5 },
        imageUrl: imageData.url,
        imageWidth: imageData.width,
        imageHeight: imageData.height,
        useSourceProportion: false,
      };
      
      // Calculate sizes and focal points using imageValues utility
      const [imageSizes, imageFocalPoints] = imageValues(imageAttrs);
      
      // Use renderImageHTML to generate proper HTML
      return renderImageHTML({
        imageId: imageData.id,
        imageUrl: imageData.url,
        imageAlt: imageData.alt || '',
        sizes: imageSizes,
        focalPoints: imageFocalPoints,
        editMode: false,
        disableLazyLoading: false,
        disable2x: false,
        scaleOnly: false,
      });
    };

    const updatePagination = (pagination) => {
      if (paginationType === 'load-more') {
        const loadMoreBtn = element.querySelector('.query-loop-load-more');
        if (loadMoreBtn) {
          if (currentPage < pagination.total_pages) {
            loadMoreBtn.style.display = 'block';
            loadMoreBtn.onclick = () => {
              currentPage++;
              fetchPosts(currentPage);
            };
          } else {
            loadMoreBtn.style.display = 'none';
          }
        }
      } else if (paginationType === 'numbered') {
        const paginationContainer = element.querySelector('.query-loop-page-numbers');
        if (paginationContainer) {
          let html = '';
          for (let i = 1; i <= pagination.total_pages; i++) {
            html += `<button class="page-number ${i === currentPage ? 'active' : ''}" data-page="${i}">${i}</button>`;
          }
          paginationContainer.innerHTML = html;
          
          paginationContainer.querySelectorAll('.page-number').forEach(btn => {
            btn.onclick = () => {
              currentPage = parseInt(btn.dataset.page);
              fetchPosts(currentPage);
              // Scroll to query loop element
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            };
          });
        }
      }
    };

    // Initial fetch
    fetchPosts(1);
  });
}

function initQueryLoopView(root) {
  initQueryLoops(root);
  initFixedBackgrounds();

  const onResize = debounce(() => {
    destroyFixedBackgrounds();
    initFixedBackgrounds();
  }, 250);
  window.addEventListener('resize', onResize);

  return function destroy() {
    window.removeEventListener('resize', onResize);
    destroyFixedBackgrounds();
  };
}

ready(initQueryLoopView, { scope: 'modulr-query-loop' });

